//
// Ordered tour sections, played on demand only. A section is playable when its
// `trigger` selector is visible. A step names an explain record (`item`) and adds
// the presentation the tour needs: `selector` overrides where the popover points
// (a page record has no anchor of its own; a look-ahead points at the area it
// announces), `showWhenAbsent` makes the step a look-ahead shown only while that
// selector is absent, `version` is the section version that introduced the step.
//
// Completion is persisted per section in settings.user.onboarding.tutos.
//

import pagePrivilegesNeeded from '../utils/pagePrivilegesNeeded';

const reportPrivileges = ['reportsSynopsis', 'reportsDetails', 'reportsHandle', 'reportsTalkToUsers'];

const tutorialRegistry = [
  {
    // Ends by walking the user into explain mode: the last steps advance on the
    // real click, so the mode is genuinely on when the tour finishes.
    //
    // Stays at version 1 on purpose. A bump would partial-replay the tail to every
    // existing user, and its copy is written for someone opening the product for
    // the first time; what is new reaches them through Nouveautés instead.
    id: 'welcome',
    version: 1,
    pageKey: 'home',
    location: '/home',
    labelKey: 'homeBasicsLabel',
    trigger: '#help',
    requires: [],
    roles: null,
    steps: [
      { item: 'welcome.intro' },
      // Skipped when the Aide menu is already unfolded, so its entries are on screen.
      {
        item: 'welcome.findHelp',
        selector: '#help',
        side: 'right',
        advanceOnClick: '#help',
        actionKey: 'welcomeFindHelpAction',
        actionTargetKey: 'helpLabel',
        actionArrow: 'Left',
        showWhenAbsent: '#explainEntry',
      },
      // Spotlights the three Aide entries it describes; only Expliquer moves on.
      {
        item: 'welcome.help',
        selector: '#helpSection',
        side: 'right',
        advanceOnClick: '#explainEntry',
        actionKey: 'welcomeHelpAction',
        actionTargetKey: 'explainLabel',
        actionArrow: 'Left',
      },
      { item: 'welcome.explain', selector: '#explainSection', side: 'right' },
      { item: 'welcome.support', selector: '#supportLink', side: 'right' },
    ],
    // A misclick must not drop someone out of their first minute in the product.
    noOutsideClose: true,
  },
  {
    id: 'dashboard',
    version: 1,
    pageKey: 'home',
    location: '/home',
    labelKey: 'dashboardLabel',
    trigger: '#tutorialDashboardCards',
    requires: null,
    roles: null,
    steps: [
      { item: 'home.dashboard.reports', side: 'left' },
      { item: 'home.dashboard.recent', side: 'left' },
      { item: 'home.dashboard.supervised', side: 'left' },
      { item: 'home.dashboard.tasks', side: 'left' },
      { item: 'home.dashboard.entrusted', side: 'left' },
      { item: 'home.dashboard.partnerships', side: 'left' },
      { item: 'home.dashboard.feedback', side: 'left' },
    ],
  },
  {
    id: 'casesList',
    version: 3,
    pageKey: 'cases',
    subPageKey: 'list',
    subLabelKey: 'list',
    location: '/cases/list',
    labelKey: 'homeCasesLabel',
    trigger: '#tutorialCasesList',
    requires: reportPrivileges,
    roles: null,
    steps: [
      { item: 'home.cases.empty', selector: '#tutorialCasesList', side: 'right', showWhenAbsent: '.tutorial-caserow' },
      { item: 'home.cases.list', side: 'right' },
      { item: 'home.cases.columns', side: 'bottom' },
      { item: 'home.cases.importance', side: 'bottom' },
      { item: 'home.cases.status', side: 'bottom', version: 2 },
      { item: 'home.cases.responders', side: 'bottom', version: 2 },
      { item: 'home.cases.info', side: 'bottom', version: 2 },
      { item: 'home.cases.create', side: 'top', version: 3 },
      { item: 'home.cases.noneSelected', showWhenAbsent: '#tutorialCaseInfo' },
    ],
  },
  {
    id: 'caseControls',
    version: 1,
    pageKey: 'cases',
    subPageKey: 'list',
    subLabelKey: 'list',
    location: '/cases/list',
    labelKey: 'caseControlsLabel',
    trigger: '#tutorialCaseInfo',
    needsSelection: true,
    playHint: 'selectReportHint',
    requires: reportPrivileges,
    roles: null,
    steps: [
      { item: 'home.cases.details', side: 'left' },
      { item: 'home.cases.badges', side: 'bottom' },
      { item: 'home.cases.category', side: 'bottom' },
      { item: 'home.cases.controls', side: 'top' },
      { item: 'home.cases.notHandled', showWhenAbsent: '#tutorialChatInput' },
    ],
  },
  {
    // The one tour that plays unasked: several discussions share a single chat
    // display, which no other messaging app does, and nothing on screen says so.
    id: 'caseMessaging',
    version: 1,
    pageKey: 'cases',
    subPageKey: 'list',
    subLabelKey: 'list',
    location: '/cases/list',
    labelKey: 'caseMessagingLabel',
    trigger: '#tutorialCaseMessaging',
    needsSelection: true,
    playHint: 'handleReportHint',
    requires: ['reportsTalkToUsers'],
    roles: null,
    steps: [
      { item: 'home.cases.messaging' },
      { item: 'home.cases.discussionSwitch', side: 'bottom' },
      { item: 'home.cases.compose', side: 'top' },
    ],
  },
  {
    id: 'caseChat',
    version: 1,
    pageKey: 'cases',
    subPageKey: 'list',
    subLabelKey: 'list',
    location: '/cases/list',
    labelKey: 'caseChatLabel',
    trigger: '#tutorialChatInput',
    needsSelection: true,
    playHint: 'handleReportHint',
    requires: ['reportsTalkToUsers'],
    roles: null,
    steps: [
      { item: 'home.cases.audio', side: 'top' },
      { item: 'home.cases.moderation', side: 'bottom' },
      { item: 'home.cases.messageMenu', side: 'left' },
      { item: 'home.cases.discussionNew', side: 'bottom' },
    ],
  },
  {
    id: 'tasksList',
    version: 1,
    pageKey: 'tasks',
    subPageKey: 'list',
    subLabelKey: 'list',
    location: '/tasks/list',
    labelKey: 'homeTasksLabel',
    // The tabs are the page's own header, so the section is playable as soon as
    // the page is open, empty list or not
    trigger: '#mine',
    requires: null,
    roles: null,
    steps: [
      { item: 'home.tasks.tabs', side: 'bottom' },
      { item: 'home.tasks.teamsFilter', side: 'bottom' },
      { item: 'home.tasks.create', side: 'left' },
      { item: 'home.tasks.list', side: 'right' },
      { item: 'home.tasks.noneSelected', showWhenAbsent: '#tutorialTaskDetails' },
    ],
  },
  {
    id: 'taskDetails',
    version: 1,
    pageKey: 'tasks',
    subPageKey: 'list',
    subLabelKey: 'list',
    location: '/tasks/list',
    labelKey: 'taskDetailsLabel',
    trigger: '#tutorialTaskDetails',
    needsSelection: true,
    playHint: 'selectTaskHint',
    requires: null,
    roles: null,
    steps: [
      { item: 'home.tasks.details', side: 'top' },
      { item: 'home.tasks.urgency', side: 'bottom' },
      { item: 'home.tasks.steps', side: 'left' },
      { item: 'home.tasks.controls', side: 'left' },
    ],
  },
  {
    id: 'qrsMenu',
    version: 1,
    pageKey: 'qrs',
    subPageKey: 'list',
    subLabelKey: 'list',
    location: '/qrs',
    labelKey: 'qrsMenuLabel',
    trigger: '#sub_list',
    requires: pagePrivilegesNeeded.qrs,
    roles: null,
    steps: [
      { item: 'qrs.list' },
      { item: 'qrs.models', selector: '#sub_models', side: 'right' },
      { item: 'qrs.printing', selector: '#sub_printing', side: 'right' },
    ],
  },
  {
    id: 'qrsList',
    version: 1,
    pageKey: 'qrs',
    subPageKey: 'list',
    subLabelKey: 'list',
    location: '/qrs/list',
    labelKey: 'qrsListLabel',
    trigger: '#qrsList',
    requires: pagePrivilegesNeeded.qrs,
    roles: null,
    steps: [
      { item: 'qrs.list.table', side: 'right' },
      { item: 'qrs.list.add', side: 'bottom' },
      { item: 'qrs.list.displayToggles', side: 'top' },
      { item: 'qrs.list.folderControls', side: 'top' },
      { item: 'qrs.list.selectHint', selector: '#qrDetails', side: 'left', showWhenAbsent: '#qrDetailsContent' },
    ],
  },
  {
    id: 'qrsDetails',
    version: 2,
    pageKey: 'qrs',
    subPageKey: 'list',
    subLabelKey: 'list',
    location: '/qrs/list',
    labelKey: 'qrsDetailsLabel',
    trigger: '#qrDetailsContent',
    needsSelection: true,
    playHint: 'selectQrHint',
    requires: pagePrivilegesNeeded.qrs,
    roles: null,
    steps: [
      { item: 'qrs.details', side: 'bottom' },
      { item: 'qrs.details.template', side: 'bottom' },
      { item: 'qrs.details.status', side: 'top' },
      { item: 'qrs.details.cases', side: 'left' },
    ],
  },
  {
    id: 'qrTemplatesList',
    version: 1,
    pageKey: 'qrs',
    subPageKey: 'models',
    subLabelKey: 'models',
    location: '/qrs/models',
    labelKey: 'qrTemplatesListLabel',
    trigger: '#qrTemplatesList',
    requires: pagePrivilegesNeeded.qrs,
    roles: null,
    steps: [
      { item: 'qrs.templates.list', side: 'right' },
      { item: 'qrs.templates.selectHint', selector: '#qrTemplateDetails', side: 'left', showWhenAbsent: '#qrTemplateDetailsContent' },
    ],
  },
  {
    id: 'qrTemplateDetails',
    version: 1,
    pageKey: 'qrs',
    subPageKey: 'models',
    subLabelKey: 'models',
    location: '/qrs/models',
    labelKey: 'qrTemplateDetailsLabel',
    trigger: '#qrTemplateDetailsContent',
    needsSelection: true,
    playHint: 'selectTemplateHint',
    requires: pagePrivilegesNeeded.qrs,
    roles: null,
    steps: [
      { item: 'qrs.templates.details', side: 'bottom' },
    ],
  },
  {
    id: 'qrsPrinting',
    version: 3,
    pageKey: 'qrs',
    subPageKey: 'printing',
    subLabelKey: 'printing',
    location: '/qrs/printing',
    labelKey: 'qrsPrintingLabel',
    trigger: '#qrsPrinting',
    requires: ['qrManage'],
    roles: null,
    steps: [
      { item: 'qrs.printing.panel', side: 'top' },
      { item: 'qrs.printing.printTab', side: 'bottom' },
      { item: 'qrs.printing.signTab', side: 'bottom' },
      { item: 'qrs.printing.layoutTab', side: 'bottom' },
      { item: 'qrs.printing.selection', side: 'bottom' },
      { item: 'qrs.printing.print', side: 'top' },
    ],
  },
  {
    // Popup-scoped: played from the poll editor's title-bar button, not the sidemenu.
    id: 'pollEdit',
    version: 2,
    popup: true,
    labelKey: 'pollEditLabel',
    requires: pagePrivilegesNeeded.polls,
    roles: null,
    steps: [
      { item: 'polls.edit.name', selector: '#pollLbl', side: 'bottom' },
      { item: 'polls.edit.items', selector: '#tutorialPollItems', side: 'right' },
      { item: 'polls.edit.form', selector: '#tutorialPollForm', side: 'top' },
      { item: 'polls.edit.params', selector: '#tutorialPollParams', side: 'left' },
      // Own anchor: the poll-details card behind the popup also renders #pollIdChk.
      { item: 'polls.edit.id', selector: '#tutorialPollIdChk', side: 'top' },
      { item: 'polls.edit.save', selector: '#savePoll', side: 'top' },
    ],
  },
  {
    id: 'stats',
    version: 2,
    pageKey: 'cases',
    subPageKey: 'stats',
    subLabelKey: 'stats',
    location: '/cases/stats',
    labelKey: 'statsLabel',
    trigger: '#tutorialStatsControls',
    requires: pagePrivilegesNeeded.stats,
    roles: null,
    steps: [
      { item: 'stats.controls', side: 'bottom' },
      { item: 'stats.overlays', side: 'bottom' },
      { item: 'stats.breakdown', side: 'top' },
    ],
  },
  {
    id: 'pollsList',
    version: 1,
    pageKey: 'polls',
    subPageKey: 'settings',
    subLabelKey: 'settings',
    location: '/polls/settings',
    labelKey: 'pollsListLabel',
    trigger: '#pollsList',
    requires: pagePrivilegesNeeded.polls,
    roles: null,
    steps: [
      { item: 'polls.list', side: 'right' },
      { item: 'polls.list.add', side: 'bottom' },
    ],
  },
  {
    id: 'pollDetails',
    version: 1,
    pageKey: 'polls',
    subPageKey: 'settings',
    subLabelKey: 'settings',
    location: '/polls/settings',
    labelKey: 'pollDetailsLabel',
    trigger: '#pollDetails',
    needsSelection: true,
    playHint: 'selectPollHint',
    requires: pagePrivilegesNeeded.polls,
    roles: null,
    steps: [
      { item: 'polls.details', side: 'bottom' },
      { item: 'polls.details.qrTab', side: 'bottom' },
    ],
  },
  {
    id: 'pollResults',
    version: 1,
    pageKey: 'polls',
    subPageKey: 'results',
    subLabelKey: 'results',
    location: '/polls/results',
    labelKey: 'pollResultsLabel',
    trigger: '#pollResultsAnswerCnt',
    needsSelection: true,
    playHint: 'pollResultsHint',
    requires: ['pollViewResults'],
    roles: null,
    steps: [
      { item: 'polls.results.selector', side: 'bottom' },
      { item: 'polls.results.count', side: 'bottom' },
    ],
  },
  {
    id: 'comms',
    version: 1,
    pageKey: 'comm',
    subPageKey: 'faq',
    subLabelKey: 'items',
    location: '/comm/faq',
    labelKey: 'commsLabel',
    trigger: '#commList',
    requires: pagePrivilegesNeeded.comms,
    roles: null,
    steps: [
      { item: 'comm.list', side: 'right' },
      { item: 'comm.list.add', side: 'bottom' },
      { item: 'comm.preview', side: 'left' },
      { item: 'comm.details', side: 'bottom' },
      { item: 'comm.details.feedback', side: 'left' },
    ],
  },
  {
    id: 'feedbacks',
    version: 1,
    pageKey: 'comm',
    subPageKey: 'feedbacks',
    subLabelKey: 'feedbacks',
    subLabelType: 'comm',
    location: '/comm/feedbacks',
    labelKey: 'feedbacksLabel',
    trigger: '#feedbacksList',
    requires: pagePrivilegesNeeded.comms,
    roles: null,
    steps: [
      { item: 'comm.feedbacks', side: 'right' },
    ],
  },
  {
    id: 'teams',
    version: 1,
    pageKey: 'responders',
    subPageKey: 'teams',
    subLabelKey: 'teams',
    location: '/responders/teams',
    labelKey: 'teamsLabel',
    trigger: '#teamsList',
    requires: pagePrivilegesNeeded.responders,
    roles: null,
    steps: [
      { item: 'responders.teams.list', side: 'right' },
      { item: 'responders.teams.add', side: 'bottom' },
    ],
  },
  {
    id: 'teamsMissions',
    version: 1,
    pageKey: 'responders',
    subPageKey: 'missions',
    subLabelKey: 'missions',
    location: '/responders/missions',
    labelKey: 'missionsLabel',
    trigger: '#teamsMissions',
    requires: pagePrivilegesNeeded.responders,
    roles: null,
    steps: [
      { item: 'responders.missions', side: 'top' },
    ],
  },
  {
    id: 'users',
    version: 2,
    pageKey: 'users',
    subPageKey: 'members',
    subLabelKey: 'members',
    location: '/users/members',
    labelKey: 'usersLabel',
    trigger: '#members',
    requires: pagePrivilegesNeeded.users,
    roles: null,
    steps: [
      { item: 'users.members.tab', side: 'bottom' },
      { item: 'users.domains.tab', side: 'bottom' },
    ],
  },
  {
    id: 'partners',
    version: 1,
    pageKey: 'partners',
    location: '/partners',
    labelKey: 'partnersLabel',
    trigger: '#partnersList',
    requires: null,
    roles: null,
    steps: [
      { item: 'partners.list', side: 'right' },
      { item: 'partners.list.add', side: 'bottom' },
    ],
  },
  {
    id: 'customization',
    version: 1,
    pageKey: 'settings',
    subPageKey: 'custom',
    subLabelKey: 'customization',
    location: '/settings/custom',
    labelKey: 'customizationLabel',
    trigger: '#customization',
    requires: pagePrivilegesNeeded.customization,
    roles: null,
    steps: [
      { item: 'settings.custom.panel', side: 'left' },
      { item: 'settings.custom.locations', side: 'right' },
      { item: 'settings.custom.branding', side: 'top' },
    ],
  },
  {
    id: 'licenses',
    version: 1,
    pageKey: 'settings',
    subPageKey: 'licenses',
    subLabelKey: 'licenses',
    location: '/settings/licenses',
    labelKey: 'licensesLabel',
    trigger: '#tutorialLicensesList',
    requires: null,
    roles: null,
    steps: [
      { item: 'settings.licenses.list', side: 'bottom' },
      // The sites tab only renders when a license carries sites; the step skips itself otherwise.
      { item: 'settings.licenses.sites', side: 'bottom' },
    ],
  },
  {
    id: 'profile',
    version: 1,
    pageKey: 'profile',
    subPageKey: 'info',
    subLabelKey: 'profile',
    location: '/profile/info',
    labelKey: 'profileLabel',
    trigger: '#profileInfo',
    requires: null,
    roles: null,
    steps: [
      { item: 'profile.language', side: 'bottom' },
      { item: 'profile.logout', side: 'bottom' },
    ],
  },
  {
    id: 'devices',
    version: 1,
    pageKey: 'profile',
    subPageKey: 'devices',
    subLabelKey: 'devices',
    location: '/profile/devices',
    labelKey: 'devicesLabel',
    trigger: '#tutorialDevicesList',
    requires: null,
    roles: null,
    steps: [
      { item: 'profile.devices.list', side: 'top' },
      { item: 'profile.devices.this', side: 'top' },
      { item: 'profile.devices.connect', side: 'bottom' },
      { item: 'profile.devices.disconnect', side: 'bottom' },
    ],
  },
  {
    id: 'security',
    version: 1,
    pageKey: 'profile',
    subPageKey: 'security',
    subLabelKey: 'security',
    location: '/profile/security',
    labelKey: 'securityLabel',
    trigger: '#password',
    requires: null,
    roles: null,
    steps: [
      { item: 'profile.password', side: 'bottom' },
      { item: 'profile.security.secondFactor', side: 'bottom' },
      // The methods table and recovery codes only render once a method is enabled; their steps skip themselves otherwise.
      { item: 'profile.security.methods', side: 'top' },
      { item: 'profile.security.add', side: 'bottom' },
      { item: 'profile.security.recoveryCodes', side: 'bottom' },
    ],
  },
];

export default tutorialRegistry;
